import { useState } from 'react';
import { balanceForPeriod } from './financial-bank-balance';
import type { FinancialOverview } from './financial-overview-api';
import { remunerationMoney } from './RemunerationColumn';
import { FinancialCalculationStatus } from './FinancialCalculationStatus';
import treasuryIcon from '../assets/finance/treasury.svg?no-inline';
import settingsIcon from '../assets/finance/settings.svg?no-inline';
import bankIllustration from '../assets/finance/treasury-bank.png';

export const treasuryColorNames = [
  'Alerte critique !',
  'Soyez vigilant !',
  'Tout va bien, mais pas de folies !',
  'Tréso saine',
  'Excellent !',
] as const;
export const treasuryColors = ['#ef3e20', '#ffcb05', '#349ab4', '#00d48d', '#ce00ef'] as const;

/** Conseil de gestion rédigé par ChatGPT : deux mois de dépenses observées,
 * hors prélèvements, puis plages indicatives. Ce n'est pas un appel IA en direct.
 * Les réglages validés restent prioritaires même si les dépenses évoluent.
 */
export function treasurySuggestions(overview: FinancialOverview): readonly string[] | null {
  const reserve = overview.remuneration?.suggestedReserveCents ?? overview.reserveTargetCents;
  if (reserve == null || BigInt(reserve) <= 0n) return null;
  const base = BigInt(reserve);
  const suggestions = [25n, 50n, 80n, 120n].map((percent) => ((base * percent) / 100n).toString());
  return suggestions.every(
    (value, index) => index === 0 || BigInt(value) > BigInt(suggestions[index - 1]!),
  )
    ? suggestions
    : null;
}

export function bankColorLevel(
  balance: string | null,
  thresholds: readonly string[] | null,
): number | null {
  return balance === null || thresholds === null
    ? null
    : thresholds.filter((threshold) => BigInt(balance) >= BigInt(threshold)).length;
}

export function TreasuryCard({
  overview,
  onSetup,
}: {
  readonly overview: FinancialOverview;
  readonly onSetup: () => void;
}): React.JSX.Element {
  const currentYear = Number(
    new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Paris', year: 'numeric' }),
  );
  const [year, setYear] = useState(Number(overview.period.slice(0, 4)));
  const thresholds = overview.treasuryColorThresholdsCents ?? treasurySuggestions(overview);
  // Une échelle stable issue des réglages : les encaissements ne déplacent pas les repères.
  const maximum = thresholds
    ? Math.max(10000, Math.ceil((Number(thresholds[3]) * 1.25) / 50000) * 50000)
    : null;
  const months = [
    'Janvier',
    'Février',
    'Mars',
    'Avril',
    'Mai',
    'Juin',
    'Juil.',
    'Août',
    'Sep.',
    'Oct.',
    'Nov.',
    'Déc.',
  ];
  return (
    <article className="treasury-card finance-card--controls" aria-label={`Trésorerie : ${year}`}>
      <header className="treasury-card__heading finance-card__heading">
        <h3 className="finance-card__title">
          <img src={treasuryIcon} alt="" />
          Trésorerie
        </h3>
        <div className="finance-card__controls">
          <select
            aria-label="Année de trésorerie"
            value={year}
            onChange={(event) => setYear(Number(event.currentTarget.value))}
          >
            {Array.from({ length: currentYear - 2019 }, (_, index) => currentYear - index).map(
              (value) => (
                <option key={value}>{value}</option>
              ),
            )}
          </select>
          <button
            type="button"
            aria-label="Configurer les couleurs de trésorerie"
            onClick={onSetup}
          >
            <img src={settingsIcon} alt="" />
          </button>
        </div>
      </header>
      <img className="treasury-card__bank" src={bankIllustration} alt="Banque japonaise" />
      <p className="treasury-card__intro">Au fil des mois :</p>
      <div className="treasury-card__months">
        {months.map((name, index) => {
          const period = `${year}-${String(index + 1).padStart(2, '0')}`;
          const row = overview.remuneration?.months.find((entry) => entry.period === period);
          const selected = balanceForPeriod(overview, period);
          const calculated = selected.provenance === 'reconstitué';
          const balance = selected.cents;
          const level = bankColorLevel(balance, thresholds);
          const width =
            balance === null || maximum === null
              ? 0
              : Math.max(0, Math.min(100, (Number(balance) / maximum) * 100));
          return (
            <div
              className="treasury-card__month"
              key={period}
              title={`${name} : ${remunerationMoney(balance)}${calculated ? `, solde Freebe reconstitué au ${selected.date}, depuis le solde du ${row?.bankAnchorDate} et les mouvements importés` : selected.date ? `, solde Freebe observé le ${selected.date}` : ', aucun solde daté pour ce mois'}`}
            >
              <div
                className={`treasury-card__rail treasury-card__level-${level ?? 'unknown'}`}
                role="img"
                aria-label={`${name} : ${remunerationMoney(balance)}${level === null ? ', non évalué' : `, ${treasuryColorNames[level]}`}`}
              >
                <i className="treasury-card__fill" style={{ width: `${width}%` }} />
                <span>{name}</span>
                <b>
                  {balance === null
                    ? period > (overview.observedThroughPeriod ?? overview.period)
                      ? 'À venir'
                      : '— · S5'
                    : remunerationMoney(balance)}
                </b>
                {[1, 2, 3, 4, 5].map((tick) => (
                  <i
                    className="treasury-card__tick"
                    key={tick}
                    style={{ left: `${(tick / 5) * 100}%` }}
                  />
                ))}
              </div>
              <div className="treasury-card__ticks" aria-hidden="true">
                {[1, 2, 3, 4, 5].map((tick) => (
                  <span key={tick}>
                    {maximum === null
                      ? '—'
                      : remunerationMoney(String(Math.round((maximum * tick) / 5)))}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {months.some((_, index) => {
        const period = `${year}-${String(index + 1).padStart(2, '0')}`;
        return (
          period <= (overview.observedThroughPeriod ?? overview.period) &&
          balanceForPeriod(overview, period).cents === null
        );
      }) ? (
        <p>
          <FinancialCalculationStatus
            calculation="cashHistory"
            label="Historique bancaire incomplet"
          />
        </p>
      ) : null}
      {overview.remuneration?.months.some(
        (row) => row.period.startsWith(String(year)) && row.calculatedBankCents != null,
      ) ? (
        <p className="treasury-card__basis">
          Historique reconstitué depuis un solde daté et les mouvements bancaires importés. Les
          graduations sont communes ; le solde de chaque mois figure à droite.
        </p>
      ) : null}
      <ul className="treasury-card__legend">
        {[4, 2, 3, 1, 0].map((level) => (
          <li key={level}>
            <i style={{ background: treasuryColors[level] }} />
            {treasuryColorNames[level]}
          </li>
        ))}
      </ul>
    </article>
  );
}
